/**
 * Configurable crew labor rates (per crew role), in cents per person-hour.
 *
 * Replaces the single hard-coded LABOR_RATE_CENTS_PER_HOUR. Each crew role
 * (lead / installer / driver / trainee) carries its own rate, edited by an
 * admin in-app. `estimate_default` is the blended rate the intake estimate
 * uses, since the crew role mix isn't known until the supervisor assigns crew
 * — actuals (where the assigned roles ARE known) cost at the per-role rates.
 */
import { z } from 'zod';
import { CrewRoleSchema } from './users.js';
/** A rate in cents per person-hour (0–$1,000/hr sanity bound). */
const RateCentsSchema = z.number().int().min(0).max(100_000);
/** Keys we store a rate for: the four crew roles plus the estimate default. */
export const LaborRateKeySchema = z.enum([
    ...CrewRoleSchema.options,
    'estimate_default',
]);
/** Full rate table — every key present (the API always returns all of them). */
export const LaborRatesSchema = z.object({
    lead: RateCentsSchema,
    installer: RateCentsSchema,
    driver: RateCentsSchema,
    trainee: RateCentsSchema,
    estimate_default: RateCentsSchema,
});
/** Admin update — any subset of the keys. */
export const LaborRatesUpdateSchema = LaborRatesSchema.partial().refine((v) => Object.keys(v).length > 0, { message: 'Provide at least one rate to update.' });
/** Look up the rate for an assigned crew member's role (falls back to default). */
export function rateForRole(rates, role) {
    return role in rates && role !== 'estimate_default'
        ? rates[role]
        : rates.estimate_default;
}
// ----------------------------------------------------------------------------
// Bid labor — crew composition (§A6f). The supervisor builds a crew from role
// lines — how many of each role AND how many hours that role works — each priced
// at its configured rate. labor = Σ(count × hours × role rate); the blended rate
// (labor ÷ man-hours) is derived for display.
// ----------------------------------------------------------------------------
/** One crew role on the job: how many, for how many hours each. */
export const CrewRoleLineSchema = z.object({
    count: z.number().int().min(0).max(99),
    hours: z.number().min(0).max(10_000),
});
/** The crew per role (count + hours), driving the blended bid rate. */
export const CrewRoleLinesSchema = z.object({
    lead: CrewRoleLineSchema,
    installer: CrewRoleLineSchema,
    driver: CrewRoleLineSchema,
    trainee: CrewRoleLineSchema,
});
export const EMPTY_CREW_ROLE_LINES = {
    lead: { count: 0, hours: 0 },
    installer: { count: 0, hours: 0 },
    driver: { count: 0, hours: 0 },
    trainee: { count: 0, hours: 0 },
};
/**
 * Blended labor for a per-role crew (each role its own count AND hours), each
 * billed at its configured rate. The blended rate (labor ÷ man-hours) is derived
 * for display. Shared by the bid editor (live total) and the API (authoritative
 * reconcile against the current rates).
 */
export function blendedLabor(lines, rates) {
    const entries = [
        ['lead', lines.lead],
        ['installer', lines.installer],
        ['driver', lines.driver],
        ['trainee', lines.trainee],
    ];
    let crew = 0;
    let manHours = 0;
    let labor = 0;
    for (const [role, line] of entries) {
        const n = Math.max(0, Math.floor(line.count || 0));
        const h = Math.max(0, line.hours || 0);
        crew += n;
        manHours += n * h;
        labor += n * h * rateForRole(rates, role);
    }
    return {
        crew,
        man_hours: Math.round(manHours * 10) / 10,
        blended_rate_cents: manHours > 0 ? Math.round(labor / manHours) : 0,
        labor_cents: Math.max(0, Math.round(labor)),
    };
}
//# sourceMappingURL=labor-rates.js.map